<template>
  <div id="app">
    <ErrorBoundary>
      <AuthWrapper ref="authWrapper">
      <template #loading>
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </template>
      
      <template #unauthenticated>
        <AppHeader :is-authenticated="false" :show-background="$route.name === 'landing'" @show-auth="redirectToHostedUI" />
        <main>
          <router-view :user="null" :user-attributes="null" @show-auth="redirectToHostedUI" @neighborhood-selected="handleUnauthenticatedNeighborhoodSelection" />
        </main>
      </template>

      <template #default="{ user, userAttributes, refreshUserAttributes }">
        <AppHeader :is-authenticated="true" :user-attributes="userAttributes" :show-background="$route.name === 'landing'" @show-auth="redirectToHostedUI" @sign-out="signOut" />
        <main>
          <router-view :user="user" :user-attributes="userAttributes" @neighborhood-selected="handleNeighborhoodSelection" @user-updated="() => handleUserUpdated(refreshUserAttributes)" />
        </main>
      </template>
    </AuthWrapper>
    </ErrorBoundary>
  </div>
</template>

<script>
import { signInWithRedirect, signOut } from 'aws-amplify/auth'
import AuthWrapper from './components/AuthWrapper.vue'
import AppHeader from './components/AppHeader.vue'
import ErrorBoundary from './components/shared/ErrorBoundary.vue'

export default {
  name: 'App',
  components: {
    AuthWrapper,
    AppHeader,
    ErrorBoundary
  },
  methods: {
    async redirectToHostedUI(tab = 'signin') {
      try {
        await signInWithRedirect()
      } catch (error) {
        console.error('Error redirecting to hosted UI:', error)
      }
    },
    handleNeighborhoodSelection(neighborhood) {
      // Store selected neighborhood and navigate to neighborhood-specific feed
      const state = neighborhood.state.charAt(0).toUpperCase() + neighborhood.state.slice(1).toLowerCase()
      const city = neighborhood.city.toLowerCase().replace(/\s+/g, '-')
      const slug = neighborhood.slug.toLowerCase().replace(/\s+/g, '-')
      this.$router.push(`/neighborhood/${state}/${city}/${slug}`)
    },
    handleUnauthenticatedNeighborhoodSelection(neighborhood) {
      // Navigate to neighborhood page - they can view but not post
      const state = neighborhood.state.charAt(0).toUpperCase() + neighborhood.state.slice(1).toLowerCase()
      const city = neighborhood.city.toLowerCase().replace(/\s+/g, '-')
      const slug = neighborhood.slug.toLowerCase().replace(/\s+/g, '-')
      this.$router.push(`/neighborhood/${state}/${city}/${slug}`)
    },
    async signOut() {
      try {
        await signOut()
      } catch (error) {
        console.error('Error signing out:', error)
      }
    },
    async handleUserUpdated(refreshUserAttributes) {
      // Use the cached refresh method instead of full reload
      if (refreshUserAttributes) {
        await refreshUserAttributes();
      } else {
        // Fallback if refresh method not available
        this.$refs.authWrapper?.checkCurrentUser?.();
      }
    }
  }
}
</script>

<style>
/* Import the original theme styles globally */
@import url('/css/style.css');

/* Mobile-friendly safe area handling for notches and camera cutouts */
#app {
  /* Safe area insets for iOS notch, Dynamic Island, and Android gestures */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);

  /* Ensure proper box-sizing for safe areas */
  box-sizing: border-box;
  min-height: 100vh;
}

/* Only apply Vue app styles to authenticated content, not landing page */
.authenticated-content {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}


main {
  padding: 0;
  margin: 0;
}

/* Ensure no gap between header and landing page content */
.landing-page {
  margin-top: 0;
  padding-top: 0;
}

/* iOS safe area support for status bar */
@supports (padding-top: env(safe-area-inset-top)) {
  #app {
    /* Additional safe area handling for iOS devices with notches */
    padding-top: max(env(safe-area-inset-top), 0px);
    padding-bottom: max(env(safe-area-inset-bottom), 0px);
  }
}
</style>